import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { SizedRelativeSpinner } from '@components/loading-spinner';
import { useIsMountedRef } from '@hooks/use-is-mounted-ref';
import { pxToRem } from '@utils/converters';

import { EditableDateCell } from './editable-date-cell';
import { EditableEmailCell } from './editable-email-cell';
import { EditableFileCell } from './editable-file-cell';
import { EditableMultiSelectCell } from './editable-multi-select-cell';
import { EditableNumberCell } from './editable-number-cell';
import { EditablePhoneCell } from './editable-phone-cell';
import { EditableSelectCell } from './editable-select-cell';
import { EditableTextCell } from './editable-text-cell';
import { EditableUserCell } from './editable-user-cell';

import { AvailableColumn, ColumnType, DataItemType } from '../types';

// table.tsx routes a column to EditableCell based on this list, so it must
// only contain types the switch below can render.
const INLINE_EDITABLE_TYPES: ReadonlyArray<ColumnType> = [
  'text',
  'email',
  'phone_number',
  'single_select',
  'multi_select',
  'date',
  'user',
  'file',
  'file_multi',
  'number',
  'decimal',
];

export const isInlineEditableType = (type?: ColumnType): boolean => {
  return Boolean(type) && INLINE_EDITABLE_TYPES.includes(type as ColumnType);
};

export type EditableCellProps = {
  column: AvailableColumn<any>;
  row: DataItemType;
  value: any;
};

export const EditableCell = ({ column, row, value }: EditableCellProps): JSX.Element => {
  const isMountedRef = useIsMountedRef();
  const [isPendingSave, setIsPendingSave] = useState(false);

  // Every editor calls column.onEdit through its own save flow, so wrapping it
  // once here — rather than in each editor — gives every field type the same
  // pending-save spinner for free.
  const wrappedColumn = useMemo<AvailableColumn<any>>(
    () => ({
      ...column,
      onEdit: column.onEdit
        ? async (editedRow, editedValue) => {
            setIsPendingSave(true);
            try {
              await column.onEdit!(editedRow, editedValue);
            } finally {
              if (isMountedRef.current) {
                setIsPendingSave(false);
              }
            }
          }
        : undefined,
    }),
    [column, isMountedRef]
  );

  const editor = (() => {
    switch (wrappedColumn.type) {
      case 'text':
        return <EditableTextCell column={wrappedColumn} row={row} value={value} />;
      case 'email':
        return <EditableEmailCell column={wrappedColumn} row={row} value={value} />;
      case 'phone_number':
        return <EditablePhoneCell column={wrappedColumn} row={row} value={value} />;
      case 'single_select':
        return <EditableSelectCell column={wrappedColumn} row={row} value={value} />;
      case 'multi_select':
        return <EditableMultiSelectCell column={wrappedColumn} row={row} value={value} />;
      case 'number':
      case 'decimal':
        return <EditableNumberCell column={wrappedColumn} row={row} value={value} />;
      case 'date':
        return <EditableDateCell column={wrappedColumn} row={row} value={value} />;
      case 'user':
        return <EditableUserCell column={wrappedColumn} row={row} value={value} />;
      case 'file':
      case 'file_multi':
        return <EditableFileCell column={wrappedColumn} row={row} value={value} />;
      default:
        return null;
    }
  })();

  return (
    <EditableCellContainer $isSaving={isPendingSave}>
      {editor}
      {isPendingSave && (
        <SavingSpinnerContainer data-c="editable-cell-saving">
          <SizedRelativeSpinner size={16} $skipMargin $borderWidth={2} />
        </SavingSpinnerContainer>
      )}
    </EditableCellContainer>
  );
};

// The spinner reserves its own 16px (plus its 8px right offset) via padding
// while saving, so it never overlaps the (possibly ellipsized) trigger text.
const EditableCellContainer = styled.div<{ $isSaving: boolean }>(({ $isSaving }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  paddingRight: $isSaving ? pxToRem(24) : 0,
}));

const SavingSpinnerContainer = styled.div({
  position: 'absolute',
  right: pxToRem(8),
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  pointerEvents: 'none',
});
